import { action, observable } from 'mobx'
import { mustBeInRange } from '@/utils/validx-util'
import { required, validationContext } from 'validx'
import { type InventoryItemPricingEditor } from './inventory-item-pricing-editor'
import {
  type InventoryItemFlatValues,
  type InventoryItemPricingValues,
} from '@/features/inventory/inventory-item-pricing-values'

/**
 * View Model for Inventory Items of type Flat.
 */
export class FlatEditorViewModel implements InventoryItemPricingEditor {
  /**
   * The client amount for the Inventory Item. This is the amount the client will
   * be charged.
   */
  @observable accessor clientAmount = 0

  /**
   * The pro payout amount for the Inventory Item. This is the amount the pro will
   * receive.
   */
  @observable accessor proPayoutAmount = 0

  /**
   * Validation context.
   */
  readonly validation = validationContext(this, {
    clientAmount: [
      required('A client price is required.'),
      mustBeInRange(0, Infinity, 'The amount must be zero or greater.'),
    ],

    proPayoutAmount: [
      required('A pro payout amount is required.'),
      mustBeInRange(0, Infinity, 'The amount must be zero or greater.'),
    ],
  })

  /**
   * @inheritdoc
   */
  validate(): boolean {
    return this.validation.reset().validate().isValid
  }

  /**
   * @inheritdoc
   */
  @action.bound
  hydrate(values: InventoryItemFlatValues): void {
    this.clientAmount = values.clientPrice
    this.proPayoutAmount = values.proPayoutAmount
  }

  /**
   * @inheritdoc
   */
  toValues(): InventoryItemPricingValues {
    return {
      type: 'Flat',
      clientPrice: this.clientAmount,
      proPayoutAmount: this.proPayoutAmount,
    }
  }

  /**
   * @inheritdoc
   */
  @action.bound
  reset() {
    this.clientAmount = 0
    this.proPayoutAmount = 0
    this.validation.reset()
  }
}
